/**
 * Loads the curated Kenyan facility list (`locations` where country == "Kenya")
 * exactly once, then serves every keystroke from memory.
 *
 * Why not query Firestore per keystroke: the collection holds ~4.9k locations
 * and a `startAt/endAt` prefix query cannot do substring or typo matching, so it
 * would cost a read per keystroke and still miss "mercy" inside "St Mercy".
 * The Kenyan slice is only ~250 docs / ~24KB slimmed, so one fetch covers the
 * whole picker: 250 reads on a cold load, 0 reads per keystroke, and 0 reads at
 * all while the localStorage copy is fresh.
 *
 * If read volume ever matters, the next step is a single manifest doc holding
 * this same array (250 reads -> 1). The shape returned here would not change.
 */

import { useEffect, useState } from "react";
import { db } from "services/firebase";

const CACHE_KEY = "kenyaFacilityListV1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // curated list changes rarely

// Module-level cache so remounts within a session never refetch.
let memoryCache = null;
let inFlight = null;

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { fetchedAt, facilities } = JSON.parse(raw);
    if (!Array.isArray(facilities) || !facilities.length) return null;
    if (Date.now() - fetchedAt > CACHE_TTL_MS) return null;
    return facilities;
  } catch {
    // Private mode / blocked site data / corrupt entry: just refetch.
    return null;
  }
};

const writeCache = (facilities) => {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ fetchedAt: Date.now(), facilities })
    );
  } catch {
    /* non-fatal: the memory cache still serves this session */
  }
};

const fetchFacilities = async () => {
  const snap = await db
    .collection("locations")
    .where("country", "==", "Kenya")
    .get();

  return snap.docs
    .map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title || data.name || "",
        county: data.county || data.state || "",
        // The curated Kenyan docs carry no postal code today; kept so the
        // picker label picks one up automatically if that changes.
        zip: data.zip || "",
        priorityTier:
          typeof data.priorityTier === "number" ? data.priorityTier : 99,
      };
    })
    .filter((f) => f.title)
    .sort((a, b) => a.title.localeCompare(b.title));
};

const useKenyaFacilities = () => {
  const [facilities, setFacilities] = useState(
    () => memoryCache || readCache() || []
  );
  const [loading, setLoading] = useState(() => !facilities.length);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (facilities.length) {
      memoryCache = facilities;
      return;
    }

    // Share one request across every mount that races here.
    inFlight = inFlight || fetchFacilities();

    inFlight
      .then((list) => {
        memoryCache = list;
        writeCache(list);
        if (!cancelled) {
          setFacilities(list);
          setLoading(false);
        }
      })
      .catch((err) => {
        inFlight = null;
        console.error("Error loading Kenyan facility list:", err);
        if (!cancelled) {
          setError(err);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { facilities, loading, error };
};

export default useKenyaFacilities;
